import { Flex } from '@chakra-ui/react';
import {
  FormFieldAbstract,
  FormFieldTitle
} from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import RichTextUpdateView from './RichTextUpdateView';
import { RichTextDiffModal } from './RichTextDiffModal';
import { RichTextDisplay } from './RichTextDisplay';
import { TextEmptyState } from '../Text/TextEmptyState';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { EntryPoint } from 'src/utils/types';

export type FormFieldText = FormFieldAbstract | FormFieldTitle;

type IProps = {
  formFieldDefinition: FormFieldText;
  value: string;
  previousValue?: string | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: string) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const GenericRichTextField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  onChange,
  availableActions,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange ? (
        <RichTextUpdateView
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: string) => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      ) : (
        <>
          {isUpdated && showDiffModal ? (
            <RichTextDiffModal
              config={formFieldDefinition}
              value={value}
              previousValue={previousValue}
              onClose={closeDiffModal}
            />
          ) : null}

          <FieldHeader
            {...formFieldDefinition}
            label={formFieldDefinition.params.labels.main}
            isUpdated={isUpdated}
            editCallback={editCallback}
            onShowDiffModal={openDiffModal}
            availableActions={availableActions}
            entryPoint={entryPoint}
            isUpdateRequired={isUpdateRequired}
          />

          {value ? (
            <RichTextDisplay textStyle={'textBody'} value={value} />
          ) : (
            <TextEmptyState />
          )}
        </>
      )}
    </Flex>
  );
};
